import { getFormValues } from 'redux-form';
import { filter } from 'ramda';
import { createSelector } from 'reselect';
import { groupBy, toArray, uniqBy } from 'lodash';

import { DateRangeWithLabel } from '@topconagriculture/react-components';
import { filterByString } from '@topconagriculture/frontend-utils';

import { RootState } from '../../reducers';
import { Forms } from '../../form/form.selector';
import { ReportsPageFormValues } from '../../../routes/Reports/ReportsPage/ReportsListPage/ReportsListPage';
import {
  CattleMovementReportItem,
  ClosedLotsReportItem,
  DMReportGroupedItem,
  DMReportItem,
  DryMatterEfficiencyItem,
  GroupDeliveryGroupedItem,
  GroupDeliveryItem,
  IngredientDryMatterSettingChangesItem,
  IngredientPricesSettingChangesItem,
  IngredientSettingChangesItem,
  IngredientUsageByPenGroupedItem,
  IngredientUsageReportItem,
  InventoryHistoryItem,
  InventoryItem,
  LoadReportData,
  LoadReportItem,
  OperatorAccuracyReportDriverItem,
  OperatorAccuracyReportItem,
  PenSettingChangesItem,
  RationDeliveryGroupedItem,
  RationDeliveryItem,
  RationSettingChangesItem,
  Report,
  ReportFilterSettings,
  ReportGridData,
  ReportInput,
  ReportViewSettings,
} from './reports.types';
import { ReportsFilterForm } from '../../../routes/Reports/ReportsPage/ReportsSpecificPage/ReportsSpecificPageToolbar/ReportsSpecificPageToolbar';
import { DatatoolReportType, InventoryReportType, ReportType } from './reports.constants';
import { filterByStringArray, getMixingTypeCodeString, sortNestedArrayByProp } from '../../../core/helpers/utils/utils';
import { toFarmTimeZone } from '../../../core/helpers/utils/dateUtils';
import { getTimeZone } from '../settings/settings.selector';

export const getReportData = (state: RootState) => state.entities.reports;
export const getDMReportData = (state: RootState) => state.entities.reports[ReportType.DMIntakePerHead];
export const getIngredientsUsageReportData = (state: RootState) =>
  state.entities.reports[ReportType.IngredientsPremixUsage];
export const getLoadReportData = (state: RootState) => state.entities.reports[ReportType.LoadReport];
export const getOperatorAccuracyReportData = (state: RootState) => state.entities.reports[ReportType.OperatorAccuracy];
export const getGroupDeliveryReportData = (state: RootState) => state.entities.reports[ReportType.GroupDelivery];
export const getRationDeliveryReportData = (state: RootState) => state.entities.reports[ReportType.RationDelivery];
export const getDryMatterEfficiencyReport = (state: RootState) =>
  state.entities.reports[ReportType.DryMatterEfficiency];
export const getCattleMovementsReportData = (state: RootState) => state.entities.reports[ReportType.CattleMovements];
export const getClosedLotsReportData = (state: RootState) => state.entities.reports[ReportType.ClosedLots];
export const isDownloadButtonDisabled = (state: RootState) => state.entities.reports.downloadButtonDisabled;
export const getFilterSettings = (state: RootState) => state.entities.reports.filterSettings;
export const getIngredientUsageByPenData = (state: RootState) =>
  state.entities.reports[ReportType.IngredientUsageByPen];
export const getInventoryReportData = (state: RootState) => state.entities.reports[InventoryReportType.Inventory];

export const getPenSettingChanges = (state: RootState) => state.entities.reports[DatatoolReportType.PenSettingChanges];
export const getRationSettingChanges = (state: RootState) =>
  state.entities.reports[DatatoolReportType.RationSettingChanges];
export const getIngredientSettingChanges = (state: RootState) =>
  state.entities.reports[DatatoolReportType.IngredientSettingChanges];
export const getIngredientPricesSettingChanges = (state: RootState) =>
  state.entities.reports[DatatoolReportType.IngredientPriceSettingChanges];
export const getIngredientDryMatterSettingChanges = (state: RootState) =>
  state.entities.reports[DatatoolReportType.IngredientDryMatterSettingChanges];
export const getIngredientInventoryChanges = (state: RootState) =>
  state.entities.reports[DatatoolReportType.IngredientInventoryChanges];

export const getReportFilters = createSelector(
  getReportData,
  (reportData) => (reportType: ReportType) => reportData[reportType]?.filters,
);

export const getSortingColumn = (state: RootState) => state.entities.reports.sortingColumn;
export const getSortingOrder = (state: RootState) => state.entities.reports.sortingOrder;

export const getSelectedTimeRange = createSelector(
  [getFormValues(Forms.reportsPageForm) as (state: RootState) => ReportsPageFormValues],
  (reportsFormValues: ReportsPageFormValues): DateRangeWithLabel | null => {
    return reportsFormValues ? reportsFormValues.selectedTimeRange : null;
  },
);

export const getDMReportsFilteredBySearch = createSelector(
  [getDMReportData, getFormValues(Forms.reportsToolbarSearchGroups)],
  (reportData: Report<DMReportItem>, filterForm: ReportsFilterForm): DMReportGroupedItem | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }

    const filteredGroups = filter(
      (group) => filterByString(group.groupLotName, (filterForm && filterForm.search) || ''),
      reportData.reportData.gridData.items,
    );

    const groupedGroups = toArray(groupBy(filteredGroups, 'groupLotId')).sort((a, b) =>
      (a[0].groupLotName || '').localeCompare(b[0].groupLotName || '', undefined, { numeric: true }),
    );

    return {
      ...reportData.reportData.gridData,
      groupedItems: groupedGroups,
    };
  },
);

export const getIngredientsUsageReportsFilteredBySearch = createSelector(
  [getIngredientsUsageReportData, getFormValues(Forms.reportsToolbarSearchGroups)],
  (
    reportData: Report<IngredientUsageReportItem>,
    filterForm: ReportsFilterForm,
  ): ReportGridData<IngredientUsageReportItem> | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }

    const filteredGroups = filter(
      (group) => filterByString(group.ingredientName, (filterForm && filterForm.search) || ''),
      reportData.reportData.gridData.items,
    );

    return {
      ...reportData.reportData.gridData,
      items: filteredGroups,
    };
  },
);
export const getCattleMovementsReportsFilteredBySearch = createSelector(
  [getCattleMovementsReportData, getFormValues(Forms.reportsToolbarSearchGroups)],
  (
    reportData: Report<CattleMovementReportItem>,
    filterForm: ReportsFilterForm,
  ): ReportGridData<CattleMovementReportItem> | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }

    const filteredGroups = filter(
      ({ lotDescription }) => filterByString(lotDescription, (filterForm && filterForm.search) || ''),
      reportData.reportData.gridData.items,
    );

    return {
      ...reportData.reportData.gridData,
      items: filteredGroups,
    };
  },
);

export const getClosedLotsReportsFilteredBySearch = createSelector(
  [getClosedLotsReportData, getFormValues(Forms.reportsToolbarSearchGroups), getFormValues(Forms.reportsPageForm)],
  (
    reportData: Report<ClosedLotsReportItem>,
    filterForm: ReportsFilterForm,
    settingsForm,
  ): ReportGridData<ClosedLotsReportItem> | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }

    const filterMode = settingsForm?.settings?.filterMode || (1 as 1 | 2);

    const filteredGroups = filter(
      ({ lotDescription }) => filterByString(lotDescription, (filterForm && filterForm.search) || ''),
      [...reportData.reportData.gridData.items].sort((a, b) => {
        switch (filterMode) {
          case 1:
            return a.startDateTime > b.startDateTime ? 1 : -1;
          case 2:
            if (!a.closeOutDateTime) {
              return 1;
            }
            return a.closeOutDateTime > b.closeOutDateTime ? 1 : -1;
          default:
            return a.startDateTime > b.startDateTime ? 1 : -1;
        }
      }),
    );

    return {
      ...reportData.reportData.gridData,
      items: filteredGroups,
    };
  },
);

export const getInventoryData = createSelector(
  [getInventoryReportData, getTimeZone],
  (reportData: Report<InventoryItem>, timezone: string): any => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }
    reportData.reportData.gridData.items.sort((i1, i2) => i1.ingredientName.localeCompare(i2.ingredientName));
    return {
      ...reportData.reportData.gridData,
      items: reportData.reportData.gridData.items.map((item) => ({
        ...item,
        orderDate: !!item.orderDate && toFarmTimeZone(new Date(item.orderDate), timezone),
      })),
    };
  },
);

export const getLastDeliveryDate = createSelector(
  [getInventoryReportData],
  (reportData: Report<InventoryItem>): string => {
    if (!reportData || !reportData.reportData.lastDelivery) {
      return '';
    }
    return reportData.reportData.lastDelivery;
  },
);

export const getInventoryReportViewSettings = createSelector(
  [getInventoryReportData],
  (reportData: Report<InventoryItem>): ReportViewSettings | undefined => reportData?.viewSettings,
);

export const getInventoryReportsFilteredBySearch = createSelector(
  [getInventoryData, getFormValues(Forms.projectedRequirementsForm)],
  (data: any, filterForm: ReportsFilterForm) => {
    if (!filterForm || !filterForm.search) {
      return data;
    }
    return {
      ...data,
      items: data.items.filter((elem) => elem.ingredientName.toLowerCase().includes(filterForm.search.toLowerCase())),
    };
  },
);

export const getLoadReportsFilteredBySearch = createSelector(
  [getLoadReportData, getFormValues(Forms.reportsToolbarSearchGroups)],
  (reportData: Report<LoadReportItem>, filterForm: ReportsFilterForm): LoadReportData[][] | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.batchGridData ||
      !reportData.reportData.batchGridData.items
    ) {
      return null;
    }

    const deliveryItems = reportData.reportData.deliveryGridData
      ? reportData.reportData.deliveryGridData.items || []
      : [];

    const filteredDeliveryItems = deliveryItems.filter((item) =>
      filterByString(item.groupName, (filterForm && filterForm.search) || ''),
    );

    let filteredReportsWithData = reportData.reportData.batchGridData.items
      .map((mainItem) => {
        const loadData = filter(
          (item) => mainItem.batchId === item.batchId,
          reportData.reportData.loadGridData ? reportData.reportData.loadGridData.items || [] : [],
        );
        const deliveryData = filter((item) => mainItem.batchId === item.batchId, filteredDeliveryItems);

        const loadDataSummary = filter(
          (item) => String(mainItem.batchId) === item.key,
          reportData.reportData.loadGridData ? reportData.reportData.loadGridData.groupSummaries || [] : [],
        );
        const deliveryDataSummary = filter(
          (item) => String(mainItem.batchId) === item.key,
          reportData.reportData.deliveryGridData ? reportData.reportData.deliveryGridData.groupSummaries || [] : [],
        );

        if (loadDataSummary[0] && loadDataSummary[0].items[0]) {
          loadDataSummary[0].items[0].deviationPerc = Number(loadDataSummary[0].items[0].deviationPerc.toFixed(2));
        }

        if (deliveryDataSummary[0] && deliveryDataSummary[0].items[0]) {
          deliveryDataSummary[0].items[0].deviationPerc = Number(
            deliveryDataSummary[0].items[0].deviationPerc.toFixed(2),
          );
        }

        const deliverySum = deliveryData.reduce(
          (acc, curr) => ({
            deliveredWeight: acc.deliveredWeight + curr.deliveredWeight,
            deviation: acc.deviation + curr.deviation,
            deviationPerc: acc.deviationPerc + Math.abs(curr.deviationPerc),
            pricePlanned: acc.pricePlanned + curr.pricePlanned,
            priceActual: acc.priceActual + curr.priceActual,
            priceError: acc.priceError + curr.priceError,
          }),
          {
            deliveredWeight: 0,
            deviation: 0,
            deviationPerc: 0,
            pricePlanned: 0,
            priceActual: 0,
            priceError: 0,
          },
        );

        const deliveryFilteredSummary = deliveryDataSummary[0]
          ? {
              ...deliveryDataSummary[0].items[0],
              deliveredWeight: deliverySum.deliveredWeight,
              deviation: deliverySum.deviation,
              deviationPerc: +(deliverySum.deviationPerc / deliveryData.length).toFixed(2),
              pricePlanned: deliverySum.pricePlanned,
              priceActual: deliverySum.priceActual,
              priceError: deliverySum.priceError,
            }
          : {};

        return {
          ...mainItem,
          loadData,
          deliveryData,
          loadDataSummary: loadDataSummary[0] ? loadDataSummary[0].items[0] : {},
          deliveryDataSummary: deliveryFilteredSummary,
          description: mainItem.description,
        };
      })
      .filter((batch) => batch.deliveryData.length > 0 || batch.loadData.length > 0);

    filteredReportsWithData = filteredReportsWithData.filter((x) =>
      filterForm && filterForm.search !== '' ? !!x.deliveryData.length && !!x.loadData.length : true,
    );

    return toArray(groupBy(filteredReportsWithData as any, 'batchDate'));
  },
);

export const getOperatorAccuracyReportsFilteredBySearch = createSelector(
  [getOperatorAccuracyReportData, getFormValues(Forms.reportsToolbarSearchGroups)],
  (
    reportData: Report<OperatorAccuracyReportItem>,
    filterForm: ReportsFilterForm,
  ): OperatorAccuracyReportDriverItem[] | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.driverGridData ||
      !reportData.reportData.driverGridData.items ||
      !reportData.reportData.groupGridData ||
      !reportData.reportData.groupGridData.summaryItems ||
      !reportData.reportData.groupGridData.items
    ) {
      return null;
    }

    const filteredDrivers = filter(
      (driver) => filterByString(driver.driverName, (filterForm && filterForm.search) || ''),
      reportData.reportData.driverGridData.items,
    );

    if (!filteredDrivers.length) {
      return null;
    }

    return reportData.reportData.driverGridData.items.map((driver) => {
      const groups = toArray(
        groupBy(
          filter(
            (group) => group.driverId === driver.driverId,
            reportData.reportData.groupGridData ? reportData.reportData.groupGridData.items : [],
          ),
          'groupId',
        ),
      ).sort((a, b) => (a[0].groupName || '').localeCompare(b[0].groupName || '', undefined, { numeric: true }));

      let summary;
      if (reportData.reportData.groupGridData && reportData.reportData.groupGridData.summaryItems) {
        summary = reportData.reportData.groupGridData.summaryItems.find(
          (summaryItem) => summaryItem.driverId === driver.driverId,
        );
      }

      return {
        ...driver,
        groups,
        summary,
      };
    });
  },
);

export const getOperatorAccuracyReportsTotals = createSelector(
  [getOperatorAccuracyReportData, getFormValues(Forms.reportsToolbarSearchGroups)],
  (reportData: Report<OperatorAccuracyReportItem>): OperatorAccuracyReportItem | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.groupGridData ||
      !reportData.reportData.groupGridData.summaryItems
    ) {
      return null;
    }

    return reportData.reportData.groupGridData.summaryItems[0];
  },
);

export const getDMReportsGroupsIds = createSelector(
  [getDMReportData],
  (reportData: Report<DMReportItem>): (undefined | string)[] => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return [];
    }

    return uniqBy<DMReportItem>(reportData.reportData.gridData.items, 'groupLotId').map((item) => {
      return item.groupLotId;
    });
  },
);

export const getGroupDeliveryReportsFilteredBySearch = createSelector(
  [getGroupDeliveryReportData, getFormValues(Forms.reportsToolbarSearchGroups)],
  (reportData: Report<GroupDeliveryItem>, filterForm: ReportsFilterForm): GroupDeliveryGroupedItem[] | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items ||
      !reportData.reportData.gridData.groupSummaries
    ) {
      return null;
    }

    const filteredGroups = filter(
      (group) => filterByString(group.groupName, (filterForm && filterForm.search) || ''),
      reportData.reportData.gridData.items,
    );

    if (!filteredGroups.length) {
      return null;
    }

    const groupedByGroupsArray = toArray(groupBy(filteredGroups, 'groupId')).sort((a, b) =>
      sortNestedArrayByProp(a, b, 'groupName'),
    );

    return groupedByGroupsArray
      .map((arrayItem) => {
        const summary =
          reportData.reportData.gridData &&
          reportData.reportData.gridData.groupSummaries &&
          reportData.reportData.gridData.groupSummaries.find(
            (summaryItem) => summaryItem.key === String(arrayItem[0].groupId),
          );

        return {
          groupName: arrayItem[0].groupName,
          items: arrayItem,
          summary: summary ? summary.items : [],
        };
      })
      .sort((a, b) => a.groupName.localeCompare(b.groupName, undefined, { numeric: true }));
  },
);

export const getRationDeliveryReportsFilteredBySearch = createSelector(
  [getRationDeliveryReportData, getFormValues(Forms.reportsToolbarSearchGroups)],
  (reportData: Report<RationDeliveryItem>, filterForm: ReportsFilterForm): RationDeliveryGroupedItem[] | null => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items ||
      !reportData.reportData.gridData.groupSummaries
    ) {
      return null;
    }

    const filteredGroups = filter(
      (group) => filterByStringArray([group.groupLotName, group.rationName], (filterForm && filterForm.search) || ''),
      reportData.reportData.gridData.items,
    );

    if (!filteredGroups.length) {
      return null;
    }

    const groupedByGroups = groupBy(filteredGroups, 'groupLotId');

    return toArray(groupedByGroups)
      .map((groupItems) => {
        const summaryItem =
          reportData &&
          reportData.reportData.gridData &&
          reportData.reportData.gridData.groupSummaries &&
          reportData.reportData.gridData.groupSummaries.find((summary) => {
            return summary.key === String(groupItems[0].groupLotId);
          });

        return {
          groupName: groupItems[0].groupLotName || '',
          items: groupItems,
          summary: summaryItem && summaryItem.items,
        };
      })
      .sort((a, b) => a.groupName.localeCompare(b.groupName, undefined, { numeric: true }));
  },
);

export const getReportTimeInput = createSelector(
  [getSelectedTimeRange],
  (selectedTimeRange: DateRangeWithLabel | null): ReportInput | null => {
    if (!selectedTimeRange) {
      return null;
    }

    return {
      from: selectedTimeRange.startDate || new Date(),
      to: selectedTimeRange.endDate || new Date(),
    };
  },
);

export const getReportsFilterData = (state: RootState) => state.entities.reports.filterSettings;

export const getDefaultReportsFilterData = createSelector(
  [getReportsFilterData],
  (filterData: ReportFilterSettings | null) => {
    if (!filterData) return {};

    return filterData;
  },
);

export const getIngredientUsageByPenDataGrouped = createSelector(
  [getIngredientUsageByPenData],
  (data): IngredientUsageByPenGroupedItem[] => {
    if (!data || !data.reportData.gridData) {
      return [];
    }

    const gridData = data.reportData.gridData;
    const items = gridData.items;
    const groupSummaries = gridData.groupSummaries || [];

    const groupedData = groupSummaries.map((x) => {
      const groupLotId = x.key;
      const groupedItems = items.filter((item) => item.groupLotId === groupLotId);
      const groupLotName = groupedItems[0].groupLotName;
      return {
        summary: x.items[0],
        groupLotId: groupLotId,
        items: groupedItems,
        groupLotName,
      };
    });

    return groupedData.sort((_o1, o2) => (o2.groupLotId === '0' ? -1 : 0));
  },
);

export const getIngredientUsageByPenDataGroupedFiltered = createSelector(
  [getIngredientUsageByPenDataGrouped, getFormValues(Forms.reportsToolbarSearchGroups)],
  (data: IngredientUsageByPenGroupedItem[], filterForm: ReportsFilterForm) => {
    if (!filterForm || !filterForm.search) {
      return data;
    }

    return data.filter((x) => x.groupLotName.toLowerCase().includes(filterForm.search.toLowerCase()));
  },
);

export const getDryMatterEfficiencyItem = createSelector(
  [getDryMatterEfficiencyReport],
  (reportData: Report<DryMatterEfficiencyItem>): any => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }
    return {
      ...reportData.reportData.gridData,
    };
  },
);

export const getPenSettingChangesItem = createSelector(
  [getPenSettingChanges],
  (reportData: Report<PenSettingChangesItem>): any => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }
    return {
      ...reportData.reportData.gridData,
    };
  },
);

export const getRationSettingChangesItem = createSelector(
  [getRationSettingChanges],
  (reportData: Report<RationSettingChangesItem>): any => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }
    return {
      ...reportData.reportData.gridData,
      items: reportData.reportData.gridData.items.map((item) => ({
        ...item,
        ingredientOldValues: item.ingredientOldValues?.map((value, index) =>
          getMixingTypeCodeString(item.ingredientFieldDescriptions[index], value),
        ),
        ingredientNewValues: item.ingredientNewValues?.map((value, index) =>
          getMixingTypeCodeString(item.ingredientFieldDescriptions[index], value),
        ),
      })),
    };
  },
);

export const getIngredientSettingChangesItem = createSelector(
  [getIngredientSettingChanges],
  (reportData: Report<IngredientSettingChangesItem>): any => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }
    return {
      ...reportData.reportData.gridData,
    };
  },
);

export const getIngredientPricesSettingChangesItem = createSelector(
  [getIngredientPricesSettingChanges],
  (reportData: Report<IngredientPricesSettingChangesItem>): any => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }
    return {
      ...reportData.reportData.gridData,
    };
  },
);

export const getIngredientDryMatterSettingChangesItem = createSelector(
  [getIngredientDryMatterSettingChanges],
  (reportData: Report<IngredientDryMatterSettingChangesItem>): any => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }
    return {
      ...reportData.reportData.gridData,
    };
  },
);

export const getIngredientInventoryChangesItem = createSelector(
  [getIngredientInventoryChanges],
  (reportData: Report<InventoryHistoryItem>) => {
    if (
      !reportData ||
      !reportData.reportData ||
      !reportData.reportData.gridData ||
      !reportData.reportData.gridData.items
    ) {
      return null;
    }
    return {
      ...reportData.reportData.gridData,
    };
  },
);
